import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
} from "@mui/material";
import { CountryCode, TimezoneName } from "countries-and-timezones";
import { t } from "i18next";
import * as React from "react";
import { useSelector } from "react-redux";
import { DayOfWeek } from "../../../../../../types/HelperTypes";
import {
  IAddress,
  IBusinessHours,
  IContactPerson,
  IContract,
  IOrganizationUnit,
  Resource,
  Scope,
} from "../../../../../../types/NendaTypes";
import { getAllPremiseCategories } from "../../../store/reducers/organizationUnitReducer";
import PermissionsGate from "../../PermissionGate";
import Address from "./Address";
import BusinessHours from "./BusinessHours";
import Commercial from "./Commercial";
import ContactPerson from "./ContactPerson";
import Contract from "./Contract";

interface PremiseInfoProps {
  premiseInfo: IOrganizationUnit;
  handleAddressChange: (address: IAddress) => void;
  handleTimezoneChange: (timezone: TimezoneName) => void;
  handleCountryChange: (country: CountryCode) => void;
  handleContactPersonChange: (contactPerson: IContactPerson) => void;
  handleContractChange: (contract: IContract) => void;
  handleCategoryChange: (category: string) => void;
  handleNumberOfEntriesChange: (entries: number) => void;
  handleDwellTimeChange: (dwellTime: number) => void;
  handleBusinessHoursChange: (
    businessHours: Partial<Record<DayOfWeek, IBusinessHours>>
  ) => void;
}

const PremiseInfo: React.FC<PremiseInfoProps> = ({
  premiseInfo,
  handleAddressChange,
  handleTimezoneChange,
  handleCountryChange,
  handleContactPersonChange,
  handleContractChange,
  handleCategoryChange,
  handleNumberOfEntriesChange,
  handleDwellTimeChange,
  handleBusinessHoursChange,
}) => {
  const existingCategories = useSelector(getAllPremiseCategories);

  return (
    <Box>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {t("form.address.label")}
        </AccordionSummary>
        <AccordionDetails>
          <Address
            address={premiseInfo.address}
            onAddressChange={handleAddressChange}
            onTimezoneChange={handleTimezoneChange}
            onCountryChange={handleCountryChange}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {t("form.contact_person")}
        </AccordionSummary>
        <AccordionDetails>
          <ContactPerson
            contactPerson={
              premiseInfo.contactPerson || {
                fullName: "",
                emailAddress: "",
                phoneNumber: "",
              }
            }
            onChange={handleContactPersonChange}
          />
        </AccordionDetails>
      </Accordion>
      <PermissionsGate
        restriction={{
          resource: Resource.Premise,
          scopes: [Scope.CanAdministrate],
        }}
      >
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            {t("form.contract")}
          </AccordionSummary>
          <AccordionDetails>
            <Contract
              contract={premiseInfo.contract}
              onChange={handleContractChange}
            />
          </AccordionDetails>
        </Accordion>
      </PermissionsGate>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {t("form.commercial")}
        </AccordionSummary>
        <AccordionDetails>
          <Commercial
            entries={premiseInfo?.numberOfEntries}
            dwellTime={premiseInfo?.dwellTimeInMinutes}
            category={premiseInfo?.category}
            categories={existingCategories}
            onCategoryChange={handleCategoryChange}
            onEntriesChange={handleNumberOfEntriesChange}
            onDwellTimeChange={handleDwellTimeChange}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {t("form.business_hours")}
        </AccordionSummary>
        <AccordionDetails>
          <BusinessHours
            businessHours={premiseInfo?.businessHours}
            onChange={handleBusinessHoursChange}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default PremiseInfo;
